// THE THEME MODE, AND WHERE IT IS REMEMBERED.
//
// E15 q1, answered "Ship a toggle" — NOT the recommended "follow the OS, no
// toggle". The recommendation was cheaper and it was not what was asked for, so
// this exists: three modes, a persisted choice, and a control in the chrome
// (src/shell/AppShell.tsx).
//
// `system` is kept as one of the three rather than replaced by the toggle. A
// two-state light/dark switch has to pick a starting side, and picking one
// overrides the reader's OS preference the first time they ever load the page —
// which is the thing `prefers-color-scheme` exists to prevent. So the default is
// `system` and the toggle cycles through it, meaning a person who has never
// touched the control still gets the palette their machine asked for.
//
// PERSISTENCE IS BEST-EFFORT AND SILENT. jsdom in this repo has no
// `window.localStorage` at all (E14-t2 found that the hard way), Safari's
// private mode throws on write, and a reader with storage disabled is not a
// reader we refuse to theme. Every access is guarded; a failure costs the
// memory, never the render.

/** The three modes `<Theme mode>` accepts. */
export type ThemeMode = 'system' | 'light' | 'dark';

export const THEME_MODE_KEY = 'docujoint.forge.theme-mode';

const MODES: readonly ThemeMode[] = ['system', 'light', 'dark'];

const isMode = (value: unknown): value is ThemeMode =>
  typeof value === 'string' && (MODES as readonly string[]).includes(value);

/**
 * The seam the tests drive, and the reason this module has no direct
 * `localStorage` reference outside `browserThemeMemory`: a component that reads
 * the global cannot be tested where the global does not exist.
 */
export interface ThemeMemory {
  read(): ThemeMode | null;
  write(mode: ThemeMode): void;
}

export const browserThemeMemory: ThemeMemory = {
  read() {
    try {
      const raw = window.localStorage?.getItem(THEME_MODE_KEY);
      return isMode(raw) ? raw : null;
    } catch {
      return null; // private mode, storage disabled, or no localStorage at all
    }
  },
  write(mode) {
    try {
      window.localStorage?.setItem(THEME_MODE_KEY, mode);
    } catch {
      /* the choice is lost, the page is not */
    }
  },
};

/** What a remembered value resolves to — an unreadable or unknown one is `system`. */
export function chooseMode(remembered: ThemeMode | null): ThemeMode {
  return isMode(remembered) ? remembered : 'system';
}

/**
 * The cycle the control walks: system → light → dark → system.
 *
 * Deliberately a CYCLE and not a two-way switch, because `system` has to stay
 * reachable. A reader who tries dark, dislikes it and wants their machine's
 * preference back must be able to get there without clearing site data.
 */
export function nextMode(mode: ThemeMode): ThemeMode {
  const at = MODES.indexOf(mode);
  return MODES[(at + 1) % MODES.length];
}

/* ── THE ARTIFACT'S THEME (Felipe's ask, 2026-08-07: "keep my dashboard
 * dark/light theme in sync with the cloud app theme") ───────────────────────
 *
 * The published dashboard is the ENGINE's document, and it has its own theme
 * seam: the render client reads `localStorage['docujoint-theme']` at boot and
 * its toggle writes the same key (engine src/render/ui/main.tsx, ui/store.ts).
 * The frame is served THROUGH THE APP'S ORIGIN (DashboardView.tsx says why),
 * so that storage is shared — writing the key here is the app handing the
 * artifact its palette through the artifact's own front door. The app never
 * reaches into the frame; that rule (DashboardView.tsx) is untouched.
 *
 * The artifact knows two themes, not three: `system` is resolved HERE, by the
 * same media query the UA would use, before it is written. */

export const ARTIFACT_THEME_KEY = 'docujoint-theme';

/** What the artifact can be told — the engine's two values. */
export type ResolvedTheme = 'light' | 'dark';

/** `system` resolved against the OS preference; explicit modes pass through. */
export function resolveTheme(mode: ThemeMode, systemPrefersDark: boolean): ResolvedTheme {
  return mode === 'system' ? (systemPrefersDark ? 'dark' : 'light') : mode;
}

/** Best-effort and silent, exactly as browserThemeMemory: a failed write costs
 *  the sync, never the page. */
export const artifactThemeMemory = {
  write(theme: ResolvedTheme): void {
    try {
      window.localStorage?.setItem(ARTIFACT_THEME_KEY, theme);
    } catch {
      /* the artifact keeps its last-known theme */
    }
  },
};

/** What the control says it will do next — the label is the ACTION, not the state. */
export function modeLabel(mode: ThemeMode): string {
  switch (mode) {
    case 'system':
      return 'Theme: follows your system';
    case 'light':
      return 'Theme: light';
    case 'dark':
      return 'Theme: dark';
  }
}
